import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { USER_PROFILE_LIST_PAGE_SIZE_OPTIONS } from './useProfileListPagination';
import './UserProfileListPanel.css';

/**
 * Snippets-tab-style bordered list container with optional pagination footer.
 */
export default function UserProfileListPanel({
  className = '',
  ariaLabel,
  loading = false,
  loadingMessage = 'Loading…',
  loadingContent = null,
  empty = false,
  emptyContent = null,
  itemCount = 0,
  pageSize = 10,
  currentPage = 1,
  totalPages = 1,
  rangeStart = 0,
  rangeEnd = 0,
  onPageSizeChange,
  onPrevPage,
  onNextPage,
  pageSizeSelectId,
  navAriaLabel = 'List pages',
  perPageAriaLabel = 'Items per page',
  children
}) {
  const showPagination = !loading && !empty && itemCount > 0 && typeof onPageSizeChange === 'function';

  return (
    <div className={`user-profile-list-panel ${className}`.trim()}>
      <div
        className="user-profile-list-panel__breakdown"
        role="region"
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        style={{ '--user-profile-list-panel-page-size': pageSize }}
      >
        {loading ? (
          loadingContent ?? (
            <p className="user-profile-list-panel__message">{loadingMessage}</p>
          )
        ) : empty ? (
          <div className="user-profile-list-panel__empty">{emptyContent}</div>
        ) : (
          <>
            <div className="user-profile-list-panel__body">{children}</div>
            {showPagination ? (
              <footer className="user-profile-list-panel__footer">
                <div className="user-profile-list-panel__footer-inner">
                  <div className="user-profile-list-panel__footer-size">
                    <label htmlFor={pageSizeSelectId} className="user-profile-list-panel__footer-label">
                      Per page
                    </label>
                    <select
                      id={pageSizeSelectId}
                      className="user-profile-list-panel__footer-select"
                      value={pageSize}
                      onChange={onPageSizeChange}
                      aria-label={perPageAriaLabel}
                    >
                      {USER_PROFILE_LIST_PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <nav className="user-profile-list-panel__footer-nav" aria-label={navAriaLabel}>
                    <button
                      type="button"
                      className="user-profile-list-panel__footer-nav-btn"
                      disabled={currentPage <= 1}
                      onClick={onPrevPage}
                      aria-label="Previous page"
                    >
                      <ChevronLeft className="user-profile-list-panel__footer-nav-icon" strokeWidth={2} />
                    </button>
                    <span className="user-profile-list-panel__footer-page-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="user-profile-list-panel__footer-nav-btn"
                      disabled={currentPage >= totalPages}
                      onClick={onNextPage}
                      aria-label="Next page"
                    >
                      <ChevronRight className="user-profile-list-panel__footer-nav-icon" strokeWidth={2} />
                    </button>
                  </nav>
                  <p className="user-profile-list-panel__footer-range" aria-live="polite">
                    Showing {rangeStart}–{rangeEnd} of {itemCount}
                  </p>
                </div>
              </footer>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
